import { useEffect, useRef, useState } from "react";
import * as SliderPrimitive from "@radix-ui/react-slider";
import { Pause, Play, RotateCcw } from "lucide-react";
import { useObjectUrl } from "@/components/sunovoice/useMicRecorder";
import { clock } from "@/lib/sunoVoicesApi";

export const MIN_SEG = 5;
export const MAX_SEG = 120;
export const segmentProblem = (range, dur) => {
  if (dur != null && dur < MIN_SEG) return `This recording is only ${Math.round(dur)} seconds — ELPM needs at least ${MIN_SEG}.`;
  const len = range[1] - range[0];
  if (len < MIN_SEG) return `Choose at least ${MIN_SEG} seconds of singing.`;
  if (len > MAX_SEG) return "Choose at most 2 minutes — the clearest part works best.";
  return null;
};

async function decodedDuration(file) {
  const ctx = new (window.AudioContext || window.webkitAudioContext)();
  try { return (await ctx.decodeAudioData(await file.arrayBuffer())).duration; } finally { ctx.close(); }
}

const thumb = "block h-5 w-5 rounded-full border-2 border-cyan-200 bg-[#0b0e1c] shadow-[0_0_12px_rgba(103,232,249,0.5)] focus-visible:outline-none";

/** Listen to the recording and choose the stretch of clear singing Suno learns from. */
export default function SegmentPicker({ file, range, onRange, onDuration, onClear, duration }) {
  const url = useObjectUrl(file);
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const adopt = (d) => { const s = Math.floor(d); onDuration(s); onRange([0, Math.min(s, 60)]); };
  const onMeta = (e) => {
    const d = e.currentTarget.duration;
    if (Number.isFinite(d)) adopt(d); else decodedDuration(file).then(adopt).catch(() => adopt(0));
  };
  useEffect(() => { setPlaying(false); }, [file]);
  const toggle = () => {
    const a = audioRef.current;
    if (playing) { a.pause(); return; }
    a.currentTime = range[0];
    a.play().catch(() => null);
  };
  const onTime = (e) => { if (e.currentTarget.currentTime >= range[1]) e.currentTarget.pause(); };
  return (
    <div data-testid="segment-picker" className="rounded-2xl border border-white/[0.08] bg-black/30 p-4">
      <audio ref={audioRef} src={url || undefined} preload="metadata" onLoadedMetadata={onMeta} onError={() => { if (url && duration == null) decodedDuration(file).then(adopt).catch(() => adopt(0)); }} onTimeUpdate={onTime} onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} data-testid="segment-audio" />
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="min-w-0 truncate text-[13px] font-semibold" data-testid="segment-file-name">{file.name}</p>
        <button type="button" data-testid="segment-clear" onClick={onClear} className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.16em] text-white/50 hover:text-white"><RotateCcw className="h-3 w-3" /> Use a different recording</button>
      </div>
      {duration == null ? <p className="mt-4 text-[12px] text-white/45">Reading the recording…</p> : duration >= MIN_SEG ? (
        <>
          <SliderPrimitive.Root data-testid="segment-slider" className="relative mt-5 flex h-5 w-full touch-none select-none items-center" min={0} max={duration} step={1} minStepsBetweenThumbs={MIN_SEG} value={range} onValueChange={onRange}>
            <SliderPrimitive.Track className="relative h-2 w-full grow overflow-hidden rounded-full bg-white/10">
              <SliderPrimitive.Range className="absolute h-full bg-gradient-to-r from-cyan-300 to-emerald-300" />
            </SliderPrimitive.Track>
            <SliderPrimitive.Thumb data-testid="segment-start-thumb" aria-label="Start" className={thumb} />
            <SliderPrimitive.Thumb data-testid="segment-end-thumb" aria-label="End" className={thumb} />
          </SliderPrimitive.Root>
          <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
            <p data-testid="segment-range-label" className="font-mono text-[11px] uppercase tracking-[0.14em] text-white/70">
              From {clock(range[0])} to {clock(range[1])} · {range[1] - range[0]} s <span className="text-white/35">of {clock(duration)}</span>
            </p>
            <button type="button" data-testid="segment-play" onClick={toggle} className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1.5 text-[12px] hover:bg-white/[0.08]">
              {playing ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />} {playing ? "Pause" : "Play this part"}
            </button>
          </div>
        </>
      ) : null}
    </div>
  );
}
